import { isEmpty } from "shared/utils";
import isArray from "lodash/isArray";
import flatten from "lodash/flatten";
import intersection from "lodash/intersection";
import { InvoicePaymentRequestStatus } from "shared/enums/InvoicePaymentRequestStatus";
import { ProgramParticipantType } from "shared/enums/ProgramParticipantType";
import ApiPurchaseOrder from "shared/model/ApiPurchaseOrder";
import { ApiProgramForDashboard } from "shared/types/ApiProgramForDashboard";
import { ApiProgramParticipantForDashboard } from "shared/types/ApiProgramParticipantForDashboard";
import { MyExpRoute } from "../routes/types";
import QueryParamKey from "shared/enums/QueryParamKey";
import { RedirectQueryParam, Route } from "shared/enums/Redirect";
import { Base64Util } from "shared/utils/base64";
import PurchaseOrderStatus from "shared/enums/PurchaseOrderStatus";
import InventoryType from "shared/enums/InventoryType";
import ApiMeetingInventoryForm from "shared/types/ApiMeetingInventoryForm";
import { ApiRegistrationParticipantForDashboardDetails } from "shared/types/ApiRegistrationParticipantForDashboardDetails";

export function getProgramParticipants(programObj: ApiProgramForDashboard) {
  const programParticipantId = programObj.programParticipants.find((o: ApiProgramParticipantForDashboard) =>
    [ProgramParticipantType.MEETING_PARTICIPANT, ProgramParticipantType.TABLETALK_PARTICIPANT].includes(
      o.programParticipantType
    )
  )?.id;
  const orgAdminProgramParticipantId = programObj.programParticipants.find(
    (o: ApiProgramParticipantForDashboard) =>
      o.programParticipantType === ProgramParticipantType.MEETING_ORGANIZATION_ADMIN_PARTICIPANT
  )?.id;
  return { programParticipantId, orgAdminProgramParticipantId };
}

export function getProgramParticipantById(
  programParticipants: Array<ApiProgramParticipantForDashboard>,
  programParticipantId: string
) {
  return programParticipants.find((o: ApiProgramParticipantForDashboard) => o.id === programParticipantId);
}

function isInvoicePaymentPending(purchaseOrder?: ApiPurchaseOrder, formId?: string) {
  if (!purchaseOrder) return false;

  return (
    (purchaseOrder.forwardedToInvoicePayer &&
      purchaseOrder.invoicePaymentRequest?.status === InvoicePaymentRequestStatus.PENDING &&
      (!formId || purchaseOrder.lines?.every(line => line.originatingEntityId === formId))) ??
    false
  );
}

export function isInvoicePaymentRequestedAndPending(
  purchaseOrders?: Array<ApiPurchaseOrder> | ApiPurchaseOrder,
  formId?: string
) {
  if (isEmpty(purchaseOrders)) return false;

  return Array.isArray(purchaseOrders)
    ? purchaseOrders.some(po => isInvoicePaymentPending(po, formId))
    : isInvoicePaymentPending(purchaseOrders, formId);
}

export function getInvoicePendingPurchaseOrder(purchaseOrders?: Array<ApiPurchaseOrder>, formId?: string) {
  return purchaseOrders?.find(po => isInvoicePaymentPending(po, formId));
}

export function getOfflinePaymentApprovalPendingPurchaseOrder(
  purchaseOrders?: Array<ApiPurchaseOrder>,
  formId?: string
) {
  return purchaseOrders?.find(po => isPaymentReceiptPendingForPO(po, formId));
}

export function getStripeReturnUrlForMyExp({
  registrationParticipantId,
  purchaseOrderId
}: {
  registrationParticipantId: string;
  purchaseOrderId: string;
}) {
  const ptUrl = new URL(window.location.href);
  ptUrl.pathname = MyExpRoute.REDIRECT.path;
  ptUrl.searchParams.append(RedirectQueryParam.ROUTE, Route.STRIPE_STATUS);
  ptUrl.searchParams.append(RedirectQueryParam.PURCHASE_ORDER_ID, purchaseOrderId);
  ptUrl.searchParams.append(QueryParamKey.REG_PARTICIPATION_ID, Base64Util.encode(registrationParticipantId) as string);
  return ptUrl.toString();
}

export function isPaymentReceiptPendingForPO(po?: ApiPurchaseOrder, formId?: string) {
  if (!po) return false;

  return (
    po.purchaseOrderStatus === PurchaseOrderStatus.CREATED &&
    po.hasRequestedOfflinePayment &&
    (!formId || po.lines?.every(line => line.originatingEntityId === formId))
  );
}

export function getOverlappingPaymentPendingInventoryForms(
  inventoryType: InventoryType | Array<InventoryType>,
  dashboardData: ApiRegistrationParticipantForDashboardDetails,
  inventoryFormsList: Array<ApiMeetingInventoryForm>,
  canHaveSinglePendingFormAtOnce?: boolean
) {
  if (!inventoryFormsList.length && !canHaveSinglePendingFormAtOnce) return { invoicePending: [], offlinePending: [] };

  const inventoryFormIdsForInvoice = isArray(inventoryType)
    ? flatten(inventoryType.map(inventory => dashboardData.pendingInvoicePaymentRequests?.[inventory] as Array<string>))
    : dashboardData.pendingInvoicePaymentRequests?.[inventoryType] ?? [];

  const inventoryFormIdsForOffline = isArray(inventoryType)
    ? flatten(inventoryType.map(inventory => dashboardData.pendingOfflinePaymentRequests?.[inventory] as Array<string>))
    : dashboardData.pendingOfflinePaymentRequests?.[inventoryType] ?? [];

  if (canHaveSinglePendingFormAtOnce) {
    return {
      invoicePending: inventoryFormIdsForInvoice,
      offlinePending: inventoryFormIdsForOffline
    };
  }

  const inventoryFormIds = inventoryFormsList.map(form => form.id);

  return {
    invoicePending: intersection(inventoryFormIds, inventoryFormIdsForInvoice),
    offlinePending: intersection(inventoryFormIds, inventoryFormIdsForOffline)
  };
}
